import { useEffect, useState } from "react";
import { useModal } from "../../../components/ui/modal/ModalProvider";
import { useToast } from "../../../components/ui/toast/ToastProvider";
import { useHelpRequests } from "../hooks/useHelpRequests";
import {
  HELP_REQUEST_STATUS,
  HELP_REQUEST_STATUS_OPTIONS,
  INCIDENT_PRIORITY,
  INCIDENT_PRIORITY_OPTIONS,
  supportEntityTypeLabel,
} from "../../../constants/supportStatus";
import { formatGraphqlError } from "../../../utils/graphqlError";

// Le contenu d'une modale est figé à l'ouverture : chaque formulaire garde son état local
// et le recopie dans `valuesRef`, lu par le bouton de validation du footer.
const useSyncedValues = (valuesRef, initial) => {
  const [values, setValues] = useState(initial);
  useEffect(() => {
    valuesRef.current = values;
  }, [values, valuesRef]);
  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));
  return [values, set];
};

export function ModalActions({ submitLabel, onSubmit, testId, type = "primary" }) {
  const { closeModal } = useModal();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit();
      closeModal();
    } catch (e) {
      toast(formatGraphqlError(e), "error", { duration: 6000 });
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn" onClick={closeModal} disabled={busy}>Annuler</button>
      <button className={`btn btn-${type}`} data-testid={testId} onClick={submit} disabled={busy}>
        {busy ? <i className="fa-solid fa-spinner fa-spin"></i> : null} {submitLabel}
      </button>
    </>
  );
}

export function CreateIncidentForm({ valuesRef, initialRequestId, assigneeOptions }) {
  const [values, set] = useSyncedValues(valuesRef, {
    requestId: initialRequestId || "",
    priority: INCIDENT_PRIORITY.MEDIUM,
    assigneeId: "",
  });
  // Demandes à traiter : statut OPEN (l'API ne dit pas si un incident existe déjà).
  const { helpRequests, loading } = useHelpRequests({ status: HELP_REQUEST_STATUS.OPEN, pageSize: 50 });
  const selected = helpRequests.find((r) => r.id === values.requestId);

  return (
    <>
      <div className="form-row">
        <label className="label">Demande d'aide</label>
        <select className="input" data-testid="tk-request" value={values.requestId} onChange={set("requestId")}>
          <option value="">{loading ? "Chargement…" : "Sélectionner une demande"}</option>
          {initialRequestId && !selected ? <option value={initialRequestId}>{initialRequestId}</option> : null}
          {helpRequests.map((r) => (
            <option key={r.id} value={r.id}>
              {r.helpRequestRef || r.id} · {r.subject}
            </option>
          ))}
        </select>
      </div>
      {selected ? (
        <div className="form-row">
          <div className="muted">{supportEntityTypeLabel(selected.entityType)}{selected.entityId ? ` · ${selected.entityId}` : ""}</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{selected.content}</div>
        </div>
      ) : null}
      <div className="form-row row-2">
        <div>
          <label className="label">Priorité</label>
          <select className="input" data-testid="tk-priority" value={values.priority} onChange={set("priority")}>
            {INCIDENT_PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Assigner à (optionnel)</label>
          <select className="input" data-testid="tk-assignee" value={values.assigneeId} onChange={set("assigneeId")}>
            <option value="">Non assigné</option>
            {assigneeOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
    </>
  );
}

export function StatusForm({ valuesRef, currentStatus }) {
  const [values, set] = useSyncedValues(valuesRef, { status: currentStatus, resolutionComment: "" });
  return (
    <>
      <div className="form-row">
        <label className="label">Nouveau statut</label>
        <select className="input" data-testid="tk-new-status" value={values.status} onChange={set("status")}>
          {HELP_REQUEST_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="form-row">
        <label className="label">Commentaire (optionnel)</label>
        <textarea className="textarea" value={values.resolutionComment} onChange={set("resolutionComment")}></textarea>
      </div>
    </>
  );
}

export function PriorityForm({ valuesRef, currentPriority }) {
  const [values, set] = useSyncedValues(valuesRef, { priority: currentPriority });
  return (
    <div className="form-row">
      <label className="label">Nouvelle priorité</label>
      <select className="input" data-testid="tk-new-priority" value={values.priority} onChange={set("priority")}>
        {INCIDENT_PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    </div>
  );
}

export function AssignForm({ valuesRef, currentAssigneeId, assigneeOptions }) {
  const [values, set] = useSyncedValues(valuesRef, { assigneeId: currentAssigneeId || "" });
  return (
    <div className="form-row">
      <label className="label">Nouvel agent</label>
      <select className="input" data-testid="tk-new-assignee" value={values.assigneeId} onChange={set("assigneeId")}>
        <option value="">Sélectionner un agent</option>
        {assigneeOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </div>
  );
}
